import type {
  ChapterPlan,
  GeneratedExpansionScene,
  MajorBuildExecutionResult,
  MajorBuildProgressEvent,
  MajorChapterBuildPlan,
  MajorChapterBuildScene,
  PreservedSourceMaterial,
} from '../types';
import {
  ALLOWED_EXPANSION_METHODS,
  FORBIDDEN_PADDING_METHODS,
  countChapterWords,
  passageLooksLikePadding,
  resolveChapterWordTargets,
  splitChapterIntoScenes,
} from './chapterExpansion.service';
import { MAJOR_LOGICAL_CALL_BUDGET, buildLogicalSceneCallId } from './scribeThinkingPolicy.service';

const SCENE_COUNT = 4;
const SMALL_GAP_FILL_MAX = 250;
const MIN_ACCEPTANCE_FLOOR_PER_SCENE = 300;

const CONFLICT_KEYWORDS =
  /\b(conflict|oppose|threat|fight|argue|refuse|block|danger|attack|betray|chase|escape|obstacle|complication)\b/i;
const DECISION_KEYWORDS = /\b(decide|decide[ds]|choose|chose|commit|agree|refuse|must|will|strategy|plan)\b/i;
const CONSEQUENCE_KEYWORDS =
  /\b(consequence|result|cost|lose|gain|because|therefore|after|changed|forced|revealed)\b/i;
const DIALOGUE_PATTERN = /["“‘][^"”’]{3,}["”’]/;
const IMAGERY_PATTERN =
  /\b(glow|shadow|smell|taste|cold|heat|dust|static|wind|sand|metal|blood|dark|light)\b/gi;

export interface SceneCompletenessResult {
  complete: boolean;
  reasons: string[];
}

export interface AssembledChapterValidation {
  valid: boolean;
  issues: string[];
  repeatedIntroductions: boolean;
  characterKnowledgeReset: boolean;
  proceduralPadding: boolean;
}

export interface GapFillDecision {
  action: 'none' | 'small_insertion' | 'regenerate_weakest';
  remainingDeficit: number;
}

export type MajorSceneGenerator = (params: {
  scene: MajorChapterBuildScene;
  plan: MajorChapterBuildPlan;
  previousEndingState?: string;
  isRetry: boolean;
  isWeakestRegeneration?: boolean;
  retryReasons?: string[];
  logicalCallNumber: number;
  logicalSceneCallId: string;
  attemptNumber: number;
  gapFillWords?: number;
}) => Promise<GeneratedExpansionScene>;

export interface MajorBuildCallbacks {
  onProgress?: (event: MajorBuildProgressEvent) => void;
}

function normalizeSentence(s: string): string {
  return s.replace(/\s+/g, ' ').trim();
}

function uniqueNonEmpty(items: string[], limit = 12): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of items) {
    const item = normalizeSentence(raw);
    if (!item || item.length < 12 || seen.has(item.toLowerCase())) continue;
    seen.add(item.toLowerCase());
    out.push(item);
    if (out.length >= limit) break;
  }
  return out;
}

export function extractPreservedSourceMaterial(draftContent: string): PreservedSourceMaterial {
  const trimmed = (draftContent || '').trim();
  const paragraphs = trimmed.split(/\n\s*\n+/).map((p) => p.trim()).filter(Boolean);
  const sentences = trimmed
    .split(/(?<=[.!?])\s+/)
    .map(normalizeSentence)
    .filter((s) => s.length > 10);

  const canonEvents = uniqueNonEmpty(
    sentences.filter((s) => /\b(went|found|saw|heard|met|took|left|arrived|opened|closed|ran|fought)\b/i.test(s)),
  );

  const usefulDialogue = uniqueNonEmpty(
    sentences.filter((s) => DIALOGUE_PATTERN.test(s)),
    8,
  );

  const decisions = uniqueNonEmpty(
    sentences.filter((s) => DECISION_KEYWORDS.test(s)),
    8,
  );

  const discoveries = uniqueNonEmpty(
    sentences.filter((s) => /\b(discover|realize|learned|understood|noticed|revealed)\b/i.test(s)),
    8,
  );

  const sceneOutcomes = uniqueNonEmpty(
    paragraphs.map((p) => {
      const parts = p.split(/(?<=[.!?])\s+/);
      return parts[parts.length - 1] || p;
    }),
    8,
  );

  const imageryCounts = new Map<string, number>();
  for (const match of trimmed.matchAll(IMAGERY_PATTERN)) {
    const word = (match[0] || '').toLowerCase();
    imageryCounts.set(word, (imageryCounts.get(word) || 0) + 1);
  }
  const establishedImagery = uniqueNonEmpty(
    sentences.filter((s) => {
      const lower = s.toLowerCase();
      return [...imageryCounts.entries()].some(([word, count]) => count >= 2 && lower.includes(word));
    }),
    6,
  );

  return {
    canonEvents,
    usefulDialogue,
    decisions,
    discoveries,
    sceneOutcomes,
    establishedImagery,
  };
}

function distributeBeatsAcrossScenes(beats: string[], sceneCount: number): string[][] {
  const buckets: string[][] = Array.from({ length: sceneCount }, () => []);
  if (beats.length === 0) return buckets;
  beats.forEach((beat, index) => {
    buckets[index % sceneCount].push(beat);
  });
  return buckets;
}

function scoreBeatDramaticWeight(beat: string, sequence: number, totalScenes: number): number {
  let weight = 1;
  const corpus = beat.toLowerCase();
  if (CONFLICT_KEYWORDS.test(corpus)) weight += 0.35;
  if (DECISION_KEYWORDS.test(corpus)) weight += 0.3;
  if (CONSEQUENCE_KEYWORDS.test(corpus)) weight += 0.25;
  if (DIALOGUE_PATTERN.test(beat)) weight += 0.15;
  if (/\b(reveal|discovery|climax|turning point|payoff)\b/i.test(corpus)) weight += 0.4;
  if (sequence === totalScenes) weight += 0.25;
  if (sequence === totalScenes - 1) weight += 0.15;
  if (sequence === 1) weight += 0.1;
  return weight;
}

export function computeSceneDramaticWeights(
  sceneBeatBuckets: string[][],
  sceneCount: number = SCENE_COUNT,
): number[] {
  const positionWeights = [0.9, 1.0, 1.1, 1.2];
  return Array.from({ length: sceneCount }, (_, index) => {
    const beats = sceneBeatBuckets[index] || [];
    const beatWeight = beats.reduce(
      (sum, beat) => sum + scoreBeatDramaticWeight(beat, index + 1, sceneCount),
      0,
    );
    const beatFactor = beats.length > 0 ? beatWeight / beats.length : 1;
    return Math.max(0.75, positionWeights[index] * beatFactor * (1 + beats.length * 0.08));
  });
}

export function normalizeWeightedWordAllocation(
  dramaticWeights: number[],
  targetTotal: number,
  perItemFloor: number,
): number[] {
  const totalWeight = dramaticWeights.reduce((sum, w) => sum + w, 0) || 1;
  const raw = dramaticWeights.map((weight) =>
    Math.max(perItemFloor, Math.round((targetTotal * weight) / totalWeight)),
  );

  let sum = raw.reduce((a, b) => a + b, 0);
  const values = [...raw];
  let cursor = values.length - 1;

  while (sum > targetTotal && cursor >= 0) {
    if (values[cursor] > perItemFloor) {
      values[cursor] -= 1;
      sum -= 1;
    } else {
      cursor -= 1;
    }
  }

  cursor = 0;
  while (sum < targetTotal) {
    values[cursor % values.length] += 1;
    sum += 1;
    cursor += 1;
  }

  return values;
}

export function allocateSceneWordBudgets(
  minimumAcceptedWords: number,
  preferredChapterTarget: number,
  dramaticWeights: number[],
): Array<{ targetMinWords: number; targetMaxWords: number; acceptanceFloorWords: number }> {
  const targetMins = normalizeWeightedWordAllocation(
    dramaticWeights,
    preferredChapterTarget,
    400,
  );
  const acceptanceFloors = normalizeWeightedWordAllocation(
    dramaticWeights,
    minimumAcceptedWords,
    MIN_ACCEPTANCE_FLOOR_PER_SCENE,
  );

  return targetMins.map((targetMinWords, index) => ({
    targetMinWords,
    targetMaxWords: Math.round(targetMinWords * 1.35),
    acceptanceFloorWords: Math.min(acceptanceFloors[index], targetMinWords),
  }));
}

function deriveSceneDramaFields(
  sequence: number,
  beats: string[],
  plan: ChapterPlan | undefined,
  preserved: PreservedSourceMaterial,
): Pick<
  MajorChapterBuildScene,
  | 'povCharacter'
  | 'location'
  | 'openingState'
  | 'dramaticGoal'
  | 'obstacle'
  | 'decision'
  | 'consequence'
  | 'continuityInputs'
  | 'forbiddenPadding'
  | 'preservedMaterialRefs'
> {
  const povCharacter = plan?.pov || plan?.characterFocus?.[0] || 'POV character';
  const location = plan?.locationFocus?.[sequence - 1] || plan?.locationFocus?.[0] || 'Current location';
  const primaryBeat = beats[0] || plan?.requiredBeats?.[sequence - 1] || plan?.summary || 'Advance chapter stakes';

  const dramaticGoal =
    sequence === 1
      ? `Establish pressure on ${povCharacter} and clarify immediate objective.`
      : sequence >= SCENE_COUNT
        ? `Deliver chapter-turn payoff: ${primaryBeat}`
        : `Escalate conflict around: ${primaryBeat}`;

  const obstacle =
    sequence === 1
      ? 'An immediate complication blocks the straightforward path.'
      : sequence === 2
        ? 'Opposition or environment resists the POV goal.'
        : sequence === 3
          ? 'A costly setback narrows available options.'
          : 'Final resistance before the chapter turn.';

  const decision =
    sequence <= 2
      ? `${povCharacter} must choose how to respond under pressure.`
      : `${povCharacter} commits to a course of action with visible cost.`;

  const consequence =
    sequence < SCENE_COUNT
      ? 'The choice changes what the characters can attempt next.'
      : 'The chapter ends with altered stakes or a new discovery.';

  const preservedMaterialRefs: string[] = [];
  if (sequence === 1 && preserved.usefulDialogue[0]) preservedMaterialRefs.push(`dialogue:${preserved.usefulDialogue[0]}`);
  if (preserved.decisions[sequence - 1]) preservedMaterialRefs.push(`decision:${preserved.decisions[sequence - 1]}`);
  if (preserved.discoveries[sequence - 1]) preservedMaterialRefs.push(`discovery:${preserved.discoveries[sequence - 1]}`);
  if (preserved.canonEvents[sequence - 1]) preservedMaterialRefs.push(`event:${preserved.canonEvents[sequence - 1]}`);
  if (preserved.establishedImagery[0]) preservedMaterialRefs.push(`imagery:${preserved.establishedImagery[0]}`);

  return {
    povCharacter,
    location,
    openingState: sequence === 1 ? 'Chapter opens from prior continuity.' : `Continues immediately from Scene ${sequence - 1} ending.`,
    dramaticGoal,
    obstacle,
    decision,
    consequence,
    continuityInputs: [
      ...(plan?.continuityNotes || []).slice(0, 2),
      ...(preserved.sceneOutcomes[sequence - 2] ? [preserved.sceneOutcomes[sequence - 2]] : []),
    ],
    forbiddenPadding: [...FORBIDDEN_PADDING_METHODS],
    preservedMaterialRefs,
  };
}

export function buildMajorChapterBuildPlan(params: {
  draftContent: string;
  plan?: ChapterPlan;
  chapterNumber: number;
  chapterLength?: string;
}): MajorChapterBuildPlan {
  const targets = resolveChapterWordTargets(params.chapterLength);
  const sourceDraftWords = countChapterWords(params.draftContent);
  const preservedSourceMaterial = extractPreservedSourceMaterial(params.draftContent);
  const requiredBeats = params.plan?.requiredBeats?.length
    ? [...params.plan.requiredBeats]
    : [
        'Opening pressure forces a response.',
        'Complication escalates interpersonal or environmental stakes.',
        'A decision with cost narrows options.',
        'Chapter-ending discovery or consequence alters next moves.',
      ];

  const beatBuckets = distributeBeatsAcrossScenes(requiredBeats, SCENE_COUNT);
  const weights = computeSceneDramaticWeights(beatBuckets);
  const preferredChapterTarget = Math.round(
    (targets.targetWordRangeMin + targets.targetWordRangeMax) / 2,
  );
  const budgets = allocateSceneWordBudgets(
    targets.minimumAcceptedWords,
    preferredChapterTarget,
    weights,
  );
  const parsedScenes = splitChapterIntoScenes(params.draftContent);

  const scenes: MajorChapterBuildScene[] = budgets.map((budget, index) => {
    const sequence = index + 1;
    const drama = deriveSceneDramaFields(sequence, beatBuckets[index], params.plan, preservedSourceMaterial);
    const sourceScene = parsedScenes[index];
    return {
      sceneId: sourceScene?.sceneId || `scene-${sequence}`,
      sequence,
      requiredBeats: beatBuckets[index],
      targetMinWords: budget.targetMinWords,
      targetMaxWords: budget.targetMaxWords,
      acceptanceFloorWords: budget.acceptanceFloorWords,
      ...drama,
    };
  });

  const preferredTargetWords = Math.round((targets.targetWordRangeMin + targets.targetWordRangeMax) / 2);

  return {
    chapterNumber: params.chapterNumber,
    minimumAcceptedWords: targets.minimumAcceptedWords,
    preferredTargetWords,
    sourceDraftWords,
    requiredBeats,
    preservedSourceMaterial,
    scenes,
  };
}

export function endsMidSentence(content: string): boolean {
  const trimmed = (content || '').trim();
  if (!trimmed) return true;
  return !/[.!?…"”']\s*$/.test(trimmed);
}

export function hasMeaningfulStateChange(openingState: string, endingState: string): boolean {
  const open = openingState.toLowerCase().trim();
  const end = endingState.toLowerCase().trim();
  if (!end || end.length < 20) return false;
  if (open === end) return false;
  const openTokens = new Set(open.split(/\W+/).filter((t) => t.length > 4));
  const endTokens = end.split(/\W+/).filter((t) => t.length > 4);
  const overlap = endTokens.filter((t) => openTokens.has(t)).length;
  return overlap / Math.max(endTokens.length, 1) < 0.85;
}

export function evaluateSceneCompleteness(
  sceneSpec: MajorChapterBuildScene,
  generated: GeneratedExpansionScene,
): SceneCompletenessResult {
  const reasons: string[] = [];

  if (generated.wordCount < sceneSpec.acceptanceFloorWords) {
    reasons.push(
      `Scene produced ${generated.wordCount} words; acceptance floor is ${sceneSpec.acceptanceFloorWords}.`,
    );
  }

  for (const beat of sceneSpec.requiredBeats) {
    const beatTokens = beat.toLowerCase().split(/\W+/).filter((t) => t.length > 4);
    const contentLower = generated.content.toLowerCase();
    const satisfied =
      generated.requiredBeatsSatisfied.includes(beat) ||
      beatTokens.some((token) => contentLower.includes(token));
    if (!satisfied) {
      reasons.push(`Required beat missing: ${beat}`);
    }
  }

  if (generated.truncated) reasons.push('Scene output was truncated.');
  if (endsMidSentence(generated.content)) reasons.push('Scene ends mid-sentence.');
  if (!generated.decisionPresent) reasons.push('Required decision is absent.');
  if (!generated.consequencePresent) reasons.push('Required consequence is absent.');
  if (!hasMeaningfulStateChange(sceneSpec.openingState, generated.endingState)) {
    reasons.push('Scene has no meaningful state change.');
  }
  if (passageLooksLikePadding(generated.content)) {
    reasons.push('Procedural or descriptive padding detected.');
  }

  return { complete: reasons.length === 0, reasons };
}

export function assembleMajorChapterScenes(
  scenes: GeneratedExpansionScene[],
  planScenes: MajorChapterBuildScene[],
): string {
  const byId = new Map(scenes.map((scene) => [scene.sceneId, scene]));
  const ordered = [...planScenes].sort((a, b) => a.sequence - b.sequence);
  return ordered
    .map((spec) => byId.get(spec.sceneId)?.content.trim() ?? '')
    .filter(Boolean)
    .join('\n\n***\n\n');
}

function jaccardSimilarity(a: string, b: string): number {
  const tokensA = new Set(a.toLowerCase().split(/\W+/).filter((t) => t.length > 3));
  const tokensB = new Set(b.toLowerCase().split(/\W+/).filter((t) => t.length > 3));
  if (tokensA.size === 0 || tokensB.size === 0) return 0;
  let intersection = 0;
  for (const token of tokensA) {
    if (tokensB.has(token)) intersection += 1;
  }
  return intersection / (tokensA.size + tokensB.size - intersection);
}

export function detectRepeatedSceneIntroductions(scenes: GeneratedExpansionScene[]): string[] {
  const issues: string[] = [];
  for (let i = 1; i < scenes.length; i++) {
    const prevIntro = scenes[i - 1].content.slice(0, 120);
    const currIntro = scenes[i].content.slice(0, 120);
    if (jaccardSimilarity(prevIntro, currIntro) > 0.55) {
      issues.push(`${scenes[i].sceneId} reintroduces material similar to the prior scene opening.`);
    }
    if (/\b(for the first time|had never|introduced to|met for the first)\b/i.test(scenes[i].content)) {
      issues.push(`${scenes[i].sceneId} may reintroduce established facts.`);
    }
  }
  return issues;
}

export function detectCharacterKnowledgeReset(scenes: GeneratedExpansionScene[]): string[] {
  const issues: string[] = [];
  const knowledgeMarkers =
    /\b(realized|knew|remembered|already|earlier|before|since|had seen|had heard)\b/gi;
  for (let i = 1; i < scenes.length; i++) {
    const prior = scenes[i - 1].endingState.toLowerCase();
    const current = scenes[i].content.toLowerCase();
    if (prior && !current.includes(prior.slice(0, 40)) && (prior.match(knowledgeMarkers) || []).length > 0) {
      if (/\b(suddenly|for the first time|never seen|did not know)\b/i.test(scenes[i].content)) {
        issues.push(`${scenes[i].sceneId} may reset character knowledge from ${scenes[i - 1].sceneId}.`);
      }
    }
  }
  return issues;
}

export function validateAssembledChapter(
  scenes: GeneratedExpansionScene[],
  plan: MajorChapterBuildPlan,
): AssembledChapterValidation {
  const issues: string[] = [];
  const repeatedIntroductions = detectRepeatedSceneIntroductions(scenes);
  const characterKnowledgeReset = detectCharacterKnowledgeReset(scenes);
  issues.push(...repeatedIntroductions, ...characterKnowledgeReset);

  const content = assembleMajorChapterScenes(scenes, plan.scenes);
  if (passageLooksLikePadding(content)) {
    issues.push('Assembled chapter contains procedural padding.');
  }

  const allBeats = plan.requiredBeats;
  const corpus = content.toLowerCase();
  for (const beat of allBeats) {
    const tokens = beat.toLowerCase().split(/\W+/).filter((t) => t.length > 4);
    if (!tokens.some((t) => corpus.includes(t))) {
      issues.push(`Chapter missing roadmap beat coverage: ${beat}`);
    }
  }

  return {
    valid: issues.length === 0,
    issues,
    repeatedIntroductions: repeatedIntroductions.length > 0,
    characterKnowledgeReset: characterKnowledgeReset.length > 0,
    proceduralPadding: passageLooksLikePadding(content),
  };
}

export function decideGapFillAction(
  assembledWordCount: number,
  minimumAcceptedWords: number,
): GapFillDecision {
  const remainingDeficit = minimumAcceptedWords - assembledWordCount;
  if (remainingDeficit <= 0) return { action: 'none', remainingDeficit: 0 };
  if (remainingDeficit <= SMALL_GAP_FILL_MAX) {
    return { action: 'small_insertion', remainingDeficit };
  }
  return { action: 'regenerate_weakest', remainingDeficit };
}

export function findWeakestSceneIndex(
  scenes: GeneratedExpansionScene[],
  planScenes: MajorChapterBuildScene[],
): number {
  let weakest = 0;
  let worstRatio = Number.POSITIVE_INFINITY;
  scenes.forEach((scene, index) => {
    const spec = planScenes.find((s) => s.sceneId === scene.sceneId);
    const min = spec?.acceptanceFloorWords || 400;
    const ratio = scene.wordCount / min;
    if (ratio < worstRatio) {
      worstRatio = ratio;
      weakest = index;
    }
  });
  return weakest;
}

export function buildSceneRetryReasonMessage(
  sceneSpec: MajorChapterBuildScene,
  completeness: SceneCompletenessResult,
): string {
  return [
    `${sceneSpec.sceneId} produced insufficient material.`,
    ...completeness.reasons.slice(0, 3),
    `Rewrite ${sceneSpec.sceneId} only.`,
  ].join(' ');
}

export function toGeneratedExpansionScene(
  sceneId: string,
  payload: {
    content: string;
    summary: string;
    endingState: string;
    requiredBeatsSatisfied?: string[];
    decisionPresent?: boolean;
    consequencePresent?: boolean;
    finishReason?: string;
    outputTokenCount?: number;
    truncated?: boolean;
  },
): GeneratedExpansionScene {
  return {
    sceneId,
    content: payload.content,
    summary: payload.summary,
    endingState: payload.endingState,
    wordCount: countChapterWords(payload.content),
    requiredBeatsSatisfied: payload.requiredBeatsSatisfied || [],
    decisionPresent: Boolean(payload.decisionPresent),
    consequencePresent: Boolean(payload.consequencePresent),
    finishReason: payload.finishReason,
    outputTokenCount: payload.outputTokenCount,
    truncated: Boolean(payload.truncated),
  };
}

export async function executeMajorChapterBuild(params: {
  plan: MajorChapterBuildPlan;
  generateScene: MajorSceneGenerator;
  callbacks?: MajorBuildCallbacks;
  title?: string;
  summary?: string;
}): Promise<MajorBuildExecutionResult> {
  const { plan, generateScene, callbacks } = params;
  let logicalCallsUsed = 0;
  const sceneResults = new Map<string, GeneratedExpansionScene>();
  const retriedScenes = new Set<string>();

  const emit = (event: MajorBuildProgressEvent) => callbacks?.onProgress?.(event);

  const generateAndTrack = async (
    scene: MajorChapterBuildScene,
    options: {
      previousEndingState?: string;
      isRetry?: boolean;
      isWeakestRegeneration?: boolean;
      retryReasons?: string[];
      attemptNumber: number;
      gapFillWords?: number;
    },
  ): Promise<GeneratedExpansionScene> => {
    if (logicalCallsUsed >= MAJOR_LOGICAL_CALL_BUDGET) {
      throw new Error('MAJOR logical call budget exceeded.');
    }
    logicalCallsUsed += 1;
    const logicalSceneCallId = buildLogicalSceneCallId(
      plan.chapterNumber,
      scene.sceneId,
      options.attemptNumber,
    );
    return generateScene({
      scene,
      plan,
      previousEndingState: options.previousEndingState,
      isRetry: Boolean(options.isRetry),
      isWeakestRegeneration: Boolean(options.isWeakestRegeneration),
      retryReasons: options.retryReasons,
      logicalCallNumber: logicalCallsUsed,
      logicalSceneCallId,
      attemptNumber: options.attemptNumber,
      gapFillWords: options.gapFillWords,
    });
  };

  let previousEndingState: string | undefined;
  for (const sceneSpec of plan.scenes) {
    emit({
      kind: 'scene_build_start',
      message: `Building Scene ${sceneSpec.sequence} of ${plan.scenes.length}...`,
      sceneId: sceneSpec.sceneId,
      sceneSequence: sceneSpec.sequence,
    });

    let generated = await generateAndTrack(sceneSpec, {
      previousEndingState,
      attemptNumber: 1,
    });

    let completeness = evaluateSceneCompleteness(sceneSpec, generated);
    if (!completeness.complete && !retriedScenes.has(sceneSpec.sceneId)) {
      retriedScenes.add(sceneSpec.sceneId);
      emit({
        kind: 'scene_retry',
        message: `Scene ${sceneSpec.sequence} needs more development. Retrying Scene ${sceneSpec.sequence} only.`,
        sceneId: sceneSpec.sceneId,
        sceneSequence: sceneSpec.sequence,
      });
      generated = await generateAndTrack(sceneSpec, {
        previousEndingState,
        isRetry: true,
        retryReasons: completeness.reasons,
        attemptNumber: 2,
      });
      completeness = evaluateSceneCompleteness(sceneSpec, generated);
    }

    sceneResults.set(sceneSpec.sceneId, generated);
    previousEndingState = generated.endingState;

    emit({
      kind: 'scene_complete',
      message: `Scene ${sceneSpec.sequence} completed: ${generated.wordCount} words.`,
      sceneId: sceneSpec.sceneId,
      sceneSequence: sceneSpec.sequence,
      wordCount: generated.wordCount,
    });
  }

  let assembledScenes = plan.scenes.map((spec) => sceneResults.get(spec.sceneId)!);
  let content = assembleMajorChapterScenes(assembledScenes, plan.scenes);
  let assembledWordCount = countChapterWords(content);

  emit({
    kind: 'assembled',
    message: `Assembled chapter: ${assembledWordCount.toLocaleString()} words.`,
    assembledWordCount,
  });

  let gapFillApplied = false;
  let weakestSceneRegenerated = false;
  let gapDecision = decideGapFillAction(assembledWordCount, plan.minimumAcceptedWords);

  if (gapDecision.action === 'small_insertion' && logicalCallsUsed < MAJOR_LOGICAL_CALL_BUDGET) {
    const weakestIndex = findWeakestSceneIndex(assembledScenes, plan.scenes);
    const weakestSpec = plan.scenes[weakestIndex];
    emit({
      kind: 'gap_fill',
      message: `Adding a final ${gapDecision.remainingDeficit}-word story beat.`,
      sceneId: weakestSpec.sceneId,
      assembledWordCount,
    });
    gapFillApplied = true;
    const gapScene = await generateAndTrack(weakestSpec, {
      previousEndingState: weakestIndex > 0 ? assembledScenes[weakestIndex - 1].endingState : undefined,
      attemptNumber: 1,
      gapFillWords: gapDecision.remainingDeficit,
    });
    const mergedContent = `${assembledScenes[weakestIndex].content.trim()}\n\n${gapScene.content.trim()}`;
    assembledScenes = assembledScenes.map((scene, index) =>
      index === weakestIndex
        ? toGeneratedExpansionScene(weakestSpec.sceneId, {
            ...scene,
            content: mergedContent,
            endingState: gapScene.endingState || scene.endingState,
            truncated: scene.truncated || gapScene.truncated,
            finishReason: gapScene.finishReason,
            outputTokenCount: gapScene.outputTokenCount,
          })
        : scene,
    );
    content = assembleMajorChapterScenes(assembledScenes, plan.scenes);
    assembledWordCount = countChapterWords(content);
    gapDecision = decideGapFillAction(assembledWordCount, plan.minimumAcceptedWords);
  }

  if (
    gapDecision.action === 'regenerate_weakest' &&
    logicalCallsUsed < MAJOR_LOGICAL_CALL_BUDGET
  ) {
    const weakestIndex = findWeakestSceneIndex(assembledScenes, plan.scenes);
    const weakestSpec = plan.scenes[weakestIndex];
    weakestSceneRegenerated = true;
    emit({
      kind: 'weakest_regen',
      message: `Scene ${weakestSpec.sequence} needs more development. Retrying Scene ${weakestSpec.sequence} only.`,
      sceneId: weakestSpec.sceneId,
      sceneSequence: weakestSpec.sequence,
    });
    const regen = await generateAndTrack(weakestSpec, {
      previousEndingState: weakestIndex > 0 ? assembledScenes[weakestIndex - 1].endingState : undefined,
      isRetry: true,
      isWeakestRegeneration: true,
      retryReasons: [`Remaining deficit ${gapDecision.remainingDeficit} words after assembly.`],
      attemptNumber: 2,
    });
    assembledScenes = assembledScenes.map((scene, index) =>
      index === weakestIndex ? regen : scene,
    );
    content = assembleMajorChapterScenes(assembledScenes, plan.scenes);
    assembledWordCount = countChapterWords(content);
  }

  const belowMinimumAfterRepair = assembledWordCount < plan.minimumAcceptedWords;

  return {
    title: params.title || `Chapter ${plan.chapterNumber}`,
    summary: params.summary || '',
    content,
    assembledWordCount,
    scenes: assembledScenes,
    logicalCallsUsed,
    gapFillApplied,
    weakestSceneRegenerated,
    belowMinimumAfterRepair,
  };
}

export function sumAcceptanceFloorWords(plan: MajorChapterBuildPlan): number {
  return plan.scenes.reduce((sum, scene) => sum + scene.acceptanceFloorWords, 0);
}

/** @deprecated use sumAcceptanceFloorWords */
export function sumSceneMinimumWords(plan: MajorChapterBuildPlan): number {
  return sumAcceptanceFloorWords(plan);
}

export function sumPreferredTargetMinimums(plan: MajorChapterBuildPlan): number {
  return plan.scenes.reduce((sum, scene) => sum + scene.targetMinWords, 0);
}

export function minimumWordsIfAllScenesPassFloors(plan: MajorChapterBuildPlan): number {
  return sumAcceptanceFloorWords(plan);
}

export function formatMajorBuildAllowedMethods(): string {
  return ALLOWED_EXPANSION_METHODS.join('; ');
}
